import React from "react";
import { action, makeObservable, observable, runInAction } from "mobx";
import { MrbBaseListViewStore } from "mrb/core";
import { MrbFilterParameters } from "mrb/core/filter";
import { MrbTableViewStore } from "mrb/components";
import { DownloadRequest } from "common/models";

function formatBytes(bytes) {
    if (!bytes) return "—";
    const k = 1024;
    const sizes = ["B", "KB", "MB", "GB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + " " + sizes[i];
}

const MUSIC_EXTS = new Set(["mp3", "wav", "flac", "aac", "m4a", "wma", "ogg", "opus"]);
const ARTWORK_EXTS = new Set(["jpg", "jpeg", "png", "gif", "webp", "tiff", "bmp", "svg", "psd"]);
const LOG_EXTS = new Set(["txt", "log"]);

function getContentTypeLabel(fileName) {
    if (!fileName) return "—";
    const ext = fileName.split(".").pop().toLowerCase();
    if (MUSIC_EXTS.has(ext)) return "Music";
    if (ARTWORK_EXTS.has(ext)) return "Artwork";
    if (LOG_EXTS.has(ext)) return "Log";
    return "—";
}

class UploadedContentSessionFilter extends MrbFilterParameters {
    @observable contentType = "";

    constructor() {
        super();
        makeObservable(this);
    }

    reset() {
        super.reset();
        this.contentType = "";
    }
}

class UploadedContentSessionViewStore extends MrbBaseListViewStore {
    @observable sessionMetadata = null;
    @observable selectedItems = [];

    contentTypeOptions = [
        { label: "ADMINISTRATION.UPLOADED_CONTENT.FILTER.CONTENT_TYPE.ALL", value: "" },
        { label: "ADMINISTRATION.UPLOADED_CONTENT.FILTER.CONTENT_TYPE.MUSIC", value: "music" },
        { label: "ADMINISTRATION.UPLOADED_CONTENT.FILTER.CONTENT_TYPE.ARTWORK", value: "artwork" },
        { label: "ADMINISTRATION.UPLOADED_CONTENT.FILTER.CONTENT_TYPE.LOG", value: "log" },
    ];

    constructor(rootStore, { routeStore }) {
        super(rootStore, {
            queryConfig: {
                filter: new UploadedContentSessionFilter(),
                orderBy: "fileName",
                orderDirection: "asc",
            },
        });
        this.routeStore = routeStore;
        makeObservable(this);

        const self = this;

        this.setGridStore(
            new MrbTableViewStore(rootStore, this.queryUtility, {
                columns: [
                    {
                        key: "fileName",
                        title: "ADMINISTRATION.UPLOADED_CONTENT.LIST.COLUMNS.FILE_NAME",
                        sortable: true,
                        flex: 1,
                        maxWidth: 300,
                        headerCheckboxSelection: true,
                        checkboxSelection: true,
                    },
                    {
                        key: "size",
                        title: "ADMINISTRATION.UPLOADED_CONTENT.LIST.COLUMNS.SIZE",
                        format: (value) => formatBytes(value),
                        sortable: true,
                        width: 80,
                        suppressSizeToFit: true,
                    },
                    {
                        key: "contentTypeDisplay",
                        title: "ADMINISTRATION.UPLOADED_CONTENT.LIST.COLUMNS.CONTENT_TYPE",
                        sortable: false,
                        width: 80,
                        suppressSizeToFit: true,
                        cell: (params) => getContentTypeLabel(params.data?.fileName),
                    },
                    {
                        key: "_download",
                        title: "",
                        sortable: false,
                        width: 100,
                        suppressSizeToFit: true,
                        cell: (params) =>
                            React.createElement(
                                "button",
                                {
                                    className: "c-btn c-btn--sml c-btn--ghost",
                                    onClick: (e) => {
                                        e.stopPropagation();
                                        self.downloadFile(params.data);
                                    },
                                },
                                "Download"
                            ),
                    },
                ],
                selection: {
                    rowSelection: "multiple",
                    isRowSelectable: () => true,
                    onRowSelected: action((node) => {
                        const fileKey = node.data.key;
                        if (node.isSelected()) {
                            if (!self.selectedItems.find((f) => f.key === fileKey)) {
                                self.selectedItems.push(node.data);
                            }
                        } else {
                            const idx = self.selectedItems.findIndex((f) => f.key === fileKey);
                            if (idx !== -1) self.selectedItems.splice(idx, 1);
                        }
                    }),
                },
                onDataChange: action(() => {
                    self.selectedItems.splice(0);
                }),
            })
        );
    }

    async find(params) {
        const result = await this.routeStore.getFiles(params);
        if (result && result.item && result.item.length > 0 && !this.sessionMetadata) {
            const first = result.item[0];
            runInAction(() => {
                this.sessionMetadata = {
                    producer: first.producer || null,
                    artist: first.artist || null,
                    album: first.album || null,
                    uploadedAt: first.uploadedAt || null,
                    fileCount: result.totalRecords,
                    totalSize: null,
                    description: null,
                };
            });
            this.routeStore.getSessionDescription().then((description) => {
                runInAction(() => {
                    if (this.sessionMetadata) {
                        this.sessionMetadata.description = description;
                    }
                });
            });
        }
        return result;
    }

    onFetchError(err) {
        this.rootStore.notificationStore.error(
            "An unexpected error occurred while loading session files.",
            err
        );
    }

    @action.bound
    changeContentTypeFilter(value) {
        this.queryUtility.filter.set("contentType", value);
    }

    @action.bound
    downloadFile(file) {
        try {
            const request = new DownloadRequest(
                file.key,
                file.fileName,
                null,
                this.routeStore.createDownloadUrl(file.key),
                null,
                file.size ?? null
            );
            this.rootStore.downloadManagerStore.addItemsToQueue([request]);
        } catch (err) {
            this.rootStore.notificationStore.error("Failed to prepare download.", err);
        }
    }

    @action.bound
    downloadSelected() {
        try {
            const requests = this.selectedItems.map(
                (file) =>
                    new DownloadRequest(
                        file.key,
                        file.fileName,
                        null,
                        this.routeStore.createDownloadUrl(file.key),
                        null,
                        file.size ?? null
                    )
            );
            this.rootStore.downloadManagerStore.addItemsToQueue(requests);
        } catch (err) {
            this.rootStore.notificationStore.error("Failed to prepare downloads.", err);
        }
    }

    @action.bound
    async downloadAll() {
        try {
            const filter = this.queryUtility.filter;
            let page = 1;
            const rpp = 200;
            let totalRecords = Infinity;
            const allItems = [];

            while ((page - 1) * rpp < totalRecords) {
                const result = await this.routeStore.getFiles({
                    pageNumber: page,
                    pageSize: rpp,
                    contentType: filter.contentType || undefined,
                    search: filter.search || undefined,
                });
                totalRecords = result.totalRecords;
                allItems.push(...result.item);
                page++;
            }

            const requests = allItems.map(
                (file) =>
                    new DownloadRequest(
                        file.key,
                        file.fileName,
                        null,
                        this.routeStore.createDownloadUrl(file.key),
                        null,
                        file.size ?? null
                    )
            );
            this.rootStore.downloadManagerStore.addItemsToQueue(requests);
        } catch (err) {
            this.rootStore.notificationStore.error("Failed to prepare downloads.", err);
        }
    }
}

export { UploadedContentSessionViewStore };
